<template>
  <v-card>
    <v-card-text v-if="!parsedData" class="py-4">
      <span>Please wait while we are parsing your file</span>
      <v-progress-linear indeterminate />
    </v-card-text>
    <template v-else>
      <v-toolbar color="primary" class="mb-4">
        <span class="text-h6">Import from VCard</span>
        <v-spacer />
        <v-btn icon @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text>
        <div class="import-form__cards-wrapper">
          <VCardImportUserInfo
            v-model="parsedData"
            :items="items"
            class="import-form__card"
          />
          <VCardImportPhoneInfo
            v-model="parsedData"
            :items="items"
            class="import-form__card"
          />
          <VCardImportHomeInfo
            v-model="parsedData"
            :items="items"
            class="import-form__card"
          />
          <VCardImportOfficeInfo
            v-model="parsedData"
            :items="items"
            class="import-form__card"
          />
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn @click="$emit('close')">Cancel</v-btn>
        <v-btn color="primary" data-track="contact.vcard.import" @click="$emit('save', parsedData)">Import</v-btn>
      </v-card-actions>
    </template>
  </v-card>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapMutations } from 'vuex';

import { NotificationMutations } from '@/store/types/mutation-types';
import { ITextValue } from '@/types/common';
import { Nullable } from '@/utils/types';
import { IParsedVCardData, IParsedVCardDataResponse } from '@/bundles/Contact/interfaces/IVCardParsedObj';
import { convertFromVCF } from '@/bundles/Contact/helpers/vcard/parseVCF';

import VCardImportUserInfo from '@/bundles/Contact/components/vcard/VCardImportUserInfo.vue';
import VCardImportPhoneInfo from '@/bundles/Contact/components/vcard/VCardImportPhoneInfo.vue';
import VCardImportHomeInfo from '@/bundles/Contact/components/vcard/VCardImportHomeInfo.vue';
import VCardImportOfficeInfo from '@/bundles/Contact/components/vcard/VCardImportOfficeInfo.vue';

interface IVCardImportDialog {
  parsedData: Nullable<IParsedVCardData>;
  items: ITextValue<string>[];
}

export default defineComponent({
  name: 'VCardImportDialog',

  components: {
    VCardImportOfficeInfo,
    VCardImportHomeInfo,
    VCardImportPhoneInfo,
    VCardImportUserInfo
  },

  props: {
    uploadedFile: {
      type: File,
      required: true,
    }
  },

  emits: ['close', 'save'],

  data: (): IVCardImportDialog => ({
    parsedData: null,
    items: [],
  }),

  mounted () {
    this.getFileData();
  },

  methods: {
    ...mapMutations('Notifications', {
      addNotification: NotificationMutations.ADD_NOTIFICATION,
    }),
    getFileData () {
      const reader = new FileReader();

      reader.onload = () => {
        convertFromVCF(reader.result as string, (error: Nullable<string>, response: Nullable<IParsedVCardDataResponse>) => {
          if (error) {
            this.addNotification({
              message: 'Error while parsing file',
              title: 'Error',
              status: 0
            });
            this.$emit('close');
            return;
          }

          const { items, ...parsedData } = response as IParsedVCardDataResponse;

          this.items = items;
          this.parsedData = parsedData;
        });
      }

      reader.onerror = () => {
        this.addNotification({
          message: 'Error while reading file',
          title: 'Error',
          status: 0
        });
        this.$emit('close');
      }

      reader.readAsText(this.uploadedFile);
    },
  }
});
</script>

<style lang="scss" scoped>
.import-form {
  &__cards-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
  }

  &__card {
    width: 100%;
  }
}
</style>
